/*
 * Copyright 2026 Sergey S. Chernov real.sergeych@gmail.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

package net.sergeych.lyng

sealed class CodeContext {
    class Module(@Suppress("unused") val packageName: String?): CodeContext()
    class Function(
        val name: String,
        val implicitThisMembers: Boolean = false,
        val implicitReceiverTypeNames: List<String> = emptyList(),
        val typeParams: Set<String> = emptySet(),
        val typeParamDecls: List<TypeDecl.TypeParam> = emptyList(),
        /** True for static methods and top-level functions: they have no implicit `this`,
         *  so class-body field initializers inside them should not inherit the class name. */
        val noImplicitThis: Boolean = false
    ): CodeContext() {
        val implicitThisTypeName: String?
            get() = implicitReceiverTypeNames.firstOrNull()
    }
    class ClassBody(val name: String, val isExtern: Boolean = false): CodeContext() {
        var typeParams: Set<String> = emptySet()
        var typeParamDecls: List<TypeDecl.TypeParam> = emptyList()
        val pendingInitializations = mutableMapOf<String, Pos>()
        val declaredMembers = mutableSetOf<String>()
        val declaredMethodNames = mutableSetOf<String>()
        val classScopeMembers = mutableSetOf<String>()
        val memberOverrides = mutableMapOf<String, Boolean>()
        val memberFieldIds = mutableMapOf<String, Int>()
        val memberMethodIds = mutableMapOf<String, Int>()
        var nextFieldId: Int = 0
        var nextMethodId: Int = 0
        var slotPlanId: Int? = null
    }
}
